import type { EstadoDescarga, ItemCola, ProgresoConversion } from './api.js';
import { describirConversion } from './eventos.js';

/** Lo mínimo para poder describir una descarga. Estructural y no `Descarga` entera porque
 *  la watchlist enseña un recorte de la descarga —el punto en el que va, no las rutas— y
 *  tenía que poder decir exactamente lo mismo que Descargas: dos vocabularios para los
 *  mismos estados es lo que hace dudar de si «En conversiones» y «Lista para colocar» son
 *  la misma cosa. */
export interface EstadoLegible {
  status: EstadoDescarga;
  sourcesTotal: number;
  sourcesXfer: number;
  errorCode: string | null;
}

/** Cada estado cuenta la verdad, y algunos exigen matizar:
 *
 *  · `queued`            → el 202 de aMule es asíncrono; sin explicarlo parece perdida.
 *  · 0 de N transfiriendo → no está rota, está en cola en otros clientes.
 *  · `import_failed`      → el FICHERO ESTÁ A SALVO; solo falta colocarlo. */
export function describirEstado(
  d: EstadoLegible,
  enCola?: ItemCola,
  progreso?: ProgresoConversion,
): { texto: string; color: 'default' | 'info' | 'success' | 'warning' | 'error'; nota?: string } {
  switch (d.status) {
    case 'queued':
      return { texto: 'En cola', color: 'default', nota: 'Enviada a aMule, preparando el fichero…' };
    case 'downloading':
      if (d.sourcesTotal > 0 && d.sourcesXfer === 0) {
        return {
          texto: 'Esperando turno', color: 'warning',
          nota: `${d.sourcesTotal} fuentes localizadas, ninguna transmitiendo todavía. No está rota: estás en cola en otros clientes.`,
        };
      }
      if (d.sourcesTotal === 0) {
        return { texto: 'Buscando fuentes', color: 'warning', nota: 'Aún no se ha localizado a nadie que tenga este fichero.' };
      }
      return { texto: 'Descargando', color: 'info' };
    case 'paused':
      return { texto: 'Pausada', color: 'default' };
    // Ni «En cola» ni «Completada»: aMule está hasheando. Con 6 GB es un minuto largo, y si
    // se reinició y está rehasheando su cola entera —los hace de uno en uno— pueden ser
    // tres cuartos de hora. Decir «Completada» ahí hacía pensar que el fichero ya estaba.
    case 'hashing':
      return {
        texto: 'Hasheando en aMule', color: 'info',
        nota:
          'Bajada del todo. aMule está comprobando el fichero y moviéndolo a su carpeta, y ' +
          'hasta que termine no se puede convertir. Con una película grande son minutos; si ' +
          'aMule acaba de reiniciarse y tiene cola, más.',
      };
    case 'completed':
      return { texto: 'Completada', color: 'success', nota: 'Descargada. Pendiente de convertir.' };
    case 'transcoding':
      // En la base de datos, esperar turno y estar bajo ffmpeg son el MISMO estado. La cola
      // en memoria es lo único que los separa, y la diferencia importa: «tardará un rato»
      // y «ni ha empezado» no se atienden igual.
      if (enCola?.estado === 'en_espera') {
        return {
          texto: `En cola para convertir · ${enCola.posicion}.ª`,
          color: 'default',
          nota:
            'Descargada y esperando turno: todavía no ha empezado a convertirse. Entra sola ' +
            'cuando quede sitio. Cuántas corren a la vez se ajusta en Ajustes → Conversión a MP4.',
        };
      }
      // Y «convirtiendo» tampoco es una sola cosa: antes de ffmpeg se analiza el original
      // y se leen por OCR los subtítulos de imagen, que puede llevar más tiempo que la
      // conversión. Decir «Convirtiendo» durante todo eso es lo que hacía parecer que se
      // había colgado.
      {
        const fase = describirConversion(progreso);
        return { texto: fase.etiqueta, color: 'info', nota: fase.nota };
      }
    case 'transcode_failed':
      return {
        texto: 'Falló la conversión', color: 'warning',
        nota: 'La descarga está íntegra: solo falló convertirla. Reintentar no vuelve a descargar nada.',
      };
    case 'ready_to_place':
      return {
        texto: 'En conversiones', color: 'success',
        nota: 'Lista y esperando en la carpeta de conversiones. Ve a Conversiones para elegir a qué biblioteca moverla.',
      };
    case 'importing':
      return { texto: 'Moviendo a la biblioteca', color: 'info' };
    case 'imported':
      return { texto: 'Lista para ver', color: 'success' };
    case 'import_failed':
      return {
        texto: 'Falta colocarla', color: 'warning',
        nota: 'El fichero está a salvo: solo falló moverlo a la biblioteca. Se reintenta desde Conversiones, y se puede elegir otra biblioteca.',
      };
    case 'failed':
      // Este código merece sus propias palabras: no falló la red ni las fuentes, es que
      // aMule perdió la descarga de su cola. Lo bajado no se puede reanudar y hay que
      // volver a pedirla, que es justo lo que no se deduce de un «Fallida» a secas.
      if (d.errorCode === 'desaparecida_de_amule') {
        // No es un final: la aplicación vuelve a pedírsela sola. Cuando lo consigue, la
        // fila pasa a «En cola» y esto deja de pintarse. Por eso es un aviso y no un error.
        return {
          texto: 'Perdida en aMule', color: 'warning',
          nota:
            'aMule dejó de tener esta descarga en su cola sin haberla terminado. Se vuelve ' +
            'a pedir sola —lo bajado no se puede reanudar, así que empieza de cero—. Si se ' +
            'queda aquí, es que volver a pedirla también está fallando.',
        };
      }
      return { texto: 'Fallida', color: 'error', nota: d.errorCode ?? undefined };
    case 'cancelled':
      return { texto: 'Cancelada', color: 'default' };
  }
}
